import { Injectable } from '@angular/core';
import bbox from '@turf/bbox';
import intersect from '@turf/intersect';
import { booleanIntersects, featureCollection } from '@turf/turf';
import type {
    Feature,
    FeatureCollection,
    GeoJsonProperties,
    Geometry,
    MultiPolygon as GeoJsonMultiPolygon,
    Polygon as GeoJsonPolygon,
    Position,
} from 'geojson';
import {
    FilterSpecification,
    GeoJSONSource,
    LayerSpecification,
    Map as MapLibreMap,
    StyleSpecification,
} from 'maplibre-gl';

import { TileSourceTypeEnum } from '@connect-services/client/services/basemap.service';
import { MapBaseService } from '@connect-services/client/services/map-base.service';
import { MultiPolygon } from '@connect-services/src/sdk/api';

export interface MaskDefinitionOptions {
    area: MultiPolygon;
    id: string;
    showMask?: boolean;
}

export interface MaskGlobalOptions {
    businessSourceIds?: string[];
    excludedSourceIds?: string[];
    hideOuterEntities?: boolean;
}

type GeoJSONSourceWithPrivateData = Omit<GeoJSONSource, '_data'> & {
    _data?: FeatureCollection | Feature | string;
};

export const NO_MATCH = '__NO_MATCH__';

export const FIELD_INTERVENTION_AREA_MASK_ID = 'field-intervention-area';
export const ZONE_DELIMITER_MASK_ID = 'zone-delimiter-mask';

export const MASK_LAYER_ID = 'generic-area-mask-layer';
export const MASK_SOURCE_ID = 'generic-area-mask-source';
export const MASK_OUTLINE_LAYER_ID = 'generic-area-outline-layer';
export const MASK_OUTLINE_SOURCE_ID = 'generic-area-outline-source';

const BASEMAP_SOURCE_IDS: string[] = Object.values(TileSourceTypeEnum);
const MASK_SERVICE_LAYER_IDS = [MASK_LAYER_ID, MASK_OUTLINE_LAYER_ID];
const MASK_SERVICE_SOURCE_IDS = [MASK_SOURCE_ID, MASK_OUTLINE_SOURCE_ID];

@Injectable({
    providedIn: 'root',
})
export class MapMaskService extends MapBaseService {
    private static readonly WORLD_RING: Position[] = [
        [-180, 90],
        [180, 90],
        [180, -90],
        [-180, -90],
        [-180, 90],
    ];
    private activeVisualMaskId: string | null = null;
    private currentRenderedVisualId: string | null = null;
    private registeredMasks = new Map<string, GeoJsonMultiPolygon>();

    public applyMask(maskConfig: MaskDefinitionOptions, globalOptions: MaskGlobalOptions = {}): void {
        if (!this.map) {
            return;
        }

        if (!this.map.isStyleLoaded()) {
            this.map.once('idle', (): void => this.applyMask(maskConfig, globalOptions));

            return;
        }

        const { id, area, showMask = false } = maskConfig;
        const geoJsonArea = this.toGeoJsonMultiPolygon(area);

        this.registeredMasks.set(id, geoJsonArea);

        const shouldUpdateActiveMask = showMask || this.activeVisualMaskId === id;
        if (shouldUpdateActiveMask) {
            this.activeVisualMaskId = showMask ? id : null;
        }

        this.updateMapState(globalOptions);
    }

    public bindMap(map: MapLibreMap): void {
        this.map = map;
    }

    public clearMasks(maskIdsList?: string[], globalOptions: MaskGlobalOptions = {}): void {
        if (!maskIdsList?.length) {
            this.registeredMasks.clear();
            this.activeVisualMaskId = null;
        } else {
            maskIdsList.forEach((id: string): void => {
                this.registeredMasks.delete(id);
                if (this.activeVisualMaskId === id) {
                    this.activeVisualMaskId = null;
                }
            });
        }

        if (!this.map) {
            return;
        }

        if (!this.map.isStyleLoaded()) {
            this.map.once('idle', (): void => this.clearMasks(maskIdsList, globalOptions));

            return;
        }

        this.updateMapState(globalOptions);
    }

    private addMaskSourceAndLayer(invertedGeoJson: FeatureCollection<GeoJsonPolygon>): void {
        if (!this.map) {
            return;
        }

        if (!this.map.isStyleLoaded()) {
            this.map.once('idle', (): void => this.addMaskSourceAndLayer(invertedGeoJson));

            return;
        }

        try {
            this.map.addSource(MASK_SOURCE_ID, {
                data: invertedGeoJson,
                type: 'geojson',
            });

            this.map.addLayer({
                id: MASK_LAYER_ID,
                paint: {
                    'fill-color': '#ffffff',
                    'fill-opacity': 0.85,
                },
                source: MASK_SOURCE_ID,
                type: 'fill',
            });
        } catch {}
    }

    private addOutlineSourceAndLayer(maskGeoJson: GeoJsonMultiPolygon): void {
        if (!this.map?.isStyleLoaded()) {
            return;
        }

        this.map.addSource(MASK_OUTLINE_SOURCE_ID, {
            data: this.buildFeatureCollection<GeoJsonMultiPolygon>(maskGeoJson),
            type: 'geojson',
        });

        this.map.addLayer({
            id: MASK_OUTLINE_LAYER_ID,
            paint: {
                'line-color': '#7f7f7f',
                'line-width': 2,
            },
            source: MASK_OUTLINE_SOURCE_ID,
            type: 'line',
        });
    }

    private buildFeatureCollection<T extends Geometry>(geometry: T): FeatureCollection<T> {
        return {
            features: [{ geometry, properties: {}, type: 'Feature' }],
            type: 'FeatureCollection',
        };
    }

    private buildWorldPolygonWithHoles(maskGeoJson: GeoJsonMultiPolygon): GeoJsonPolygon {
        const holes: Position[][] = maskGeoJson.coordinates
            .map((polygonCoordinates: Position[][]): Position[] => polygonCoordinates[0])
            .filter((coords: Position[]): coords is Position[] => !!coords?.length);

        return {
            coordinates: [MapMaskService.WORLD_RING, ...holes],
            type: 'Polygon',
        };
    }

    private clearVisualMask(): void {
        if (!this.map) {
            return;
        }

        MASK_SERVICE_LAYER_IDS.forEach((id: string): void => this.removeLayerIfExists(id));
        MASK_SERVICE_SOURCE_IDS.forEach((id: string): void => this.removeSourceIfExists(id));
        this.currentRenderedVisualId = null;
    }

    private combineWithExistingFilter(
        existingFilter: FilterSpecification | void | undefined,
        maskFilter: FilterSpecification,
    ): FilterSpecification {
        if (!existingFilter) {
            return maskFilter;
        }

        if (Array.isArray(existingFilter) && existingFilter[0] === 'all') {
            const cleanedConditions = existingFilter
                .slice(1)
                .filter((condition): boolean => !this.isMaskFilterCondition(condition));

            return ['all', ...cleanedConditions, maskFilter] as FilterSpecification;
        }

        if (!this.isMaskFilterCondition(existingFilter)) {
            return ['all', existingFilter, maskFilter] as FilterSpecification;
        }

        return maskFilter;
    }

    private createInvertedMaskGeoJson(maskGeoJson: GeoJsonMultiPolygon): FeatureCollection<GeoJsonPolygon> {
        const worldPolygon = this.buildWorldPolygonWithHoles(maskGeoJson);

        return this.buildFeatureCollection<GeoJsonPolygon>(worldPolygon);
    }

    private filterEntitiesCumulative(excludedSourceIds: string[]): void {
        const map = this.map;
        if (!map) {
            return;
        }

        const combinedMask = this.getCombinedMaskGeometry();
        const targetSources: string[] = this.getEligibleBusinessSources(excludedSourceIds);

        targetSources.forEach((sourceId: string): void => {
            const source = map.getSource(sourceId) as GeoJSONSource | undefined;
            if (!source) {
                return;
            }

            const sourceData = (source as GeoJSONSourceWithPrivateData)._data as FeatureCollection;

            if (!sourceData?.features || sourceData.features.length === 0) {
                return;
            }

            const validFeatures: Feature<Geometry, GeoJsonProperties>[] = combinedMask
                ? sourceData.features.filter((feature: Feature<Geometry, GeoJsonProperties>): boolean => {
                      const featureBbox = bbox(feature);
                      const maskBbox = bbox(combinedMask);

                      const hasBboxOverlap: boolean = !(
                          featureBbox[0] > maskBbox[2] ||
                          featureBbox[2] < maskBbox[0] ||
                          featureBbox[1] > maskBbox[3] ||
                          featureBbox[3] < maskBbox[1]
                      );

                      if (!hasBboxOverlap) {
                          return false;
                      }

                      return booleanIntersects(feature, combinedMask);
                  })
                : [];

            const sampleProps =
                sourceData.features.find(
                    (feature: Feature<Geometry, GeoJsonProperties>): GeoJsonProperties => feature.properties,
                )?.properties ?? {};
            const idKey: string | undefined = ['entityId', 'uuid', 'id'].find(
                (key: string): boolean => key in sampleProps,
            );

            let validIds: string[];
            let maskFilterExpression: FilterSpecification;

            if (idKey) {
                validIds = validFeatures
                    .map((feature: Feature<Geometry, GeoJsonProperties>): string => feature.properties?.[idKey])
                    .filter((id: string): id is string => id !== undefined && id !== null);

                maskFilterExpression =
                    validIds.length > 0
                        ? (['in', ['get', idKey], ['literal', validIds]] as FilterSpecification)
                        : (['in', ['get', idKey], ['literal', [NO_MATCH]]] as FilterSpecification);
            } else {
                validIds = validFeatures
                    .map((feature: Feature<Geometry, GeoJsonProperties>): string | number | undefined => feature.id)
                    .filter((id: string | number | undefined): id is string => id !== undefined && id !== null);
                maskFilterExpression =
                    validIds.length > 0
                        ? (['in', ['id'], ['literal', validIds]] as FilterSpecification)
                        : (['in', ['id'], ['literal', [NO_MATCH]]] as FilterSpecification);
            }

            this.getLayersForSource(sourceId).forEach((layerId: string): void => {
                const existingFilter: void | FilterSpecification = map.getFilter(layerId);
                const combinedFilter: FilterSpecification = this.combineWithExistingFilter(
                    existingFilter,
                    maskFilterExpression,
                );

                map.setFilter(layerId, combinedFilter);
            });
        });
    }

    private getCombinedMaskGeometry(): GeoJsonMultiPolygon | null {
        const masks = Array.from(this.registeredMasks.values());

        if (!masks.length) {
            return null;
        }

        if (masks.length === 1) {
            return masks[0];
        }

        let combinedPolygon: Feature<GeoJsonPolygon | GeoJsonMultiPolygon> = featureCollection([
            { geometry: masks[0], properties: {}, type: 'Feature' },
        ]).features[0];

        for (let i: number = 1; i < masks.length; i++) {
            const nextPoly: Feature<GeoJsonPolygon | GeoJsonMultiPolygon> = {
                geometry: masks[i],
                properties: {},
                type: 'Feature',
            };

            const intersectionResult = intersect(featureCollection([combinedPolygon, nextPoly]));

            if (!intersectionResult) {
                return null;
            }

            combinedPolygon = intersectionResult as Feature<GeoJsonPolygon | GeoJsonMultiPolygon>;
        }

        const geom = combinedPolygon.geometry;
        if (geom.type === 'Polygon') {
            return {
                coordinates: [geom.coordinates],
                type: 'MultiPolygon',
            };
        }

        return geom as GeoJsonMultiPolygon;
    }

    private getEligibleBusinessSources(excludedSourceIds: string[]): string[] {
        if (!this.map) {
            return [];
        }

        const style: StyleSpecification = this.map.getStyle();
        if (!style?.sources) {
            return [];
        }

        const allIgnoredSources: string[] = [...BASEMAP_SOURCE_IDS, ...MASK_SERVICE_SOURCE_IDS, ...excludedSourceIds];

        return Object.keys(style.sources).filter((sourceId: string): boolean => {
            if (sourceId.includes('mapbox-gl-draw') || sourceId.includes('gl-draw')) {
                return false;
            }

            return !allIgnoredSources.includes(sourceId);
        });
    }

    private getLayersForSource(sourceId: string): string[] {
        if (!this.map) {
            return [];
        }

        const style: StyleSpecification = this.map.getStyle();
        if (!style?.layers) {
            return [];
        }

        return style.layers
            .filter((layer: LayerSpecification): boolean => 'source' in layer && layer.source === sourceId)
            .map((layer: LayerSpecification): string => layer.id);
    }

    private isMaskFilterCondition(filter: unknown): boolean {
        if (!Array.isArray(filter) || filter[0] !== 'in') {
            return false;
        }

        const target = filter[1];

        const isPropertyInFilter: boolean =
            Array.isArray(target) && target[0] === 'get' && ['entityId', 'uuid', 'id'].includes(target[1] as string);

        const isRootIdInFilter: boolean = Array.isArray(target) && target[0] === 'id';

        return isPropertyInFilter || isRootIdInFilter;
    }

    private moveMaskBelowBusinessLayers(businessSourceIds: string[] = []): void {
        if (!this.map?.getLayer(MASK_LAYER_ID)) {
            return;
        }

        const firstBusinessLayer: LayerSpecification | undefined = this.map
            .getStyle()
            .layers.find((layer: LayerSpecification): boolean => {
                if (
                    !('source' in layer) ||
                    BASEMAP_SOURCE_IDS.includes(layer.source as string) ||
                    MASK_SERVICE_LAYER_IDS.includes(layer.id)
                ) {
                    return false;
                }

                if (businessSourceIds.length) {
                    return businessSourceIds.includes(layer.source);
                }

                return true;
            });

        if (!firstBusinessLayer) {
            return;
        }

        this.map.moveLayer(MASK_LAYER_ID, firstBusinessLayer.id);
        if (this.map.getLayer(MASK_OUTLINE_LAYER_ID)) {
            this.map.moveLayer(MASK_OUTLINE_LAYER_ID, firstBusinessLayer.id);
        }
    }

    private removeLayerIfExists(layerId: string): void {
        if (!this.map?.getLayer(layerId)) {
            return;
        }

        this.map.removeLayer(layerId);
    }

    private removeSourceIfExists(sourceId: string): void {
        if (!this.map?.getSource(sourceId)) {
            return;
        }

        this.map.removeSource(sourceId);
    }

    private renderVisualMask(maskGeoJson: GeoJsonMultiPolygon, businessSourceIds: string[]): void {
        if (!this.map) {
            return;
        }

        const invertedGeoJson = this.createInvertedMaskGeoJson(maskGeoJson);
        const maskSource = this.map.getSource(MASK_SOURCE_ID) as GeoJSONSource | undefined;

        if (maskSource) {
            maskSource.setData(invertedGeoJson);

            const outlineSource = this.map.getSource(MASK_OUTLINE_SOURCE_ID) as GeoJSONSource | undefined;
            if (outlineSource) {
                outlineSource.setData(this.buildFeatureCollection<GeoJsonMultiPolygon>(maskGeoJson));
            }

            return;
        }

        this.addMaskSourceAndLayer(invertedGeoJson);
        this.addOutlineSourceAndLayer(maskGeoJson);
        try {
            this.moveMaskBelowBusinessLayers(businessSourceIds);
        } catch {}
    }

    private resetEntitiesFilter(excludedSourceIds: string[]): void {
        const map = this.map;
        if (!map) {
            return;
        }

        const targetSources: string[] = this.getEligibleBusinessSources(excludedSourceIds);

        targetSources.forEach((sourceId: string): void => {
            this.getLayersForSource(sourceId).forEach((layerId: string): void => {
                const existingFilter: void | FilterSpecification = map.getFilter(layerId);
                if (!existingFilter) {
                    return;
                }

                if (Array.isArray(existingFilter) && existingFilter[0] === 'all') {
                    const cleanedConditions = existingFilter
                        .slice(1)
                        .filter((condition): boolean => !this.isMaskFilterCondition(condition));

                    switch (cleanedConditions.length) {
                        case 0:
                            map.setFilter(layerId, null);
                            break;
                        case 1:
                            map.setFilter(layerId, cleanedConditions[0] as FilterSpecification);
                            break;
                        default:
                            map.setFilter(layerId, ['all', ...cleanedConditions] as FilterSpecification);
                            break;
                    }

                    return;
                }

                if (!this.isMaskFilterCondition(existingFilter)) {
                    return;
                }

                map.setFilter(layerId, null);
            });
        });
    }

    private toGeoJsonMultiPolygon(area: MultiPolygon | GeoJsonMultiPolygon): GeoJsonMultiPolygon {
        return {
            coordinates: area.coordinates,
            type: 'MultiPolygon',
        };
    }

    private updateMapState(globalOptions: MaskGlobalOptions): void {
        const { hideOuterEntities = true, excludedSourceIds = [], businessSourceIds = [] } = globalOptions;

        if (hideOuterEntities && this.registeredMasks.size) {
            this.filterEntitiesCumulative(excludedSourceIds);
        } else {
            this.resetEntitiesFilter(excludedSourceIds);
        }

        if (this.activeVisualMaskId && this.registeredMasks.has(this.activeVisualMaskId)) {
            const visualMaskGeoJson: GeoJsonMultiPolygon = this.registeredMasks.get(this.activeVisualMaskId)!;

            this.renderVisualMask(visualMaskGeoJson, businessSourceIds);
            this.currentRenderedVisualId = this.activeVisualMaskId;

            return;
        }

        this.clearVisualMask();
        this.currentRenderedVisualId = null;
    }
}
